import React from "react";

// ClubCrest — the club identity mark used in the person-first inbox (Turn 7).
//
// The platform has no club logo/crest asset today (channel.data.users carries
// only club_name + club_id — no image URL), so this renders a deterministic
// two-tone CSS shield seeded off the club name, matching the design's generated
// crests. If a real logoUrl ever lands it is rendered instead, no caller change.
//
// Variants:
//   "shield" — inline mark for a second line / club label (default 13×15).
//   "badge"  — docks onto an avatar corner (person-first mini-badge), sized in
//              em off the avatar's font-size, with the white halo + hairline
//              ring identical to the flag-docking pattern in the design.
//   "card"   — the large crest in the ClubCard popover (with club initials).

const hashString = (str) => {
  let hash = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const crestGradient = (seed) => {
  const hue = hashString(seed) % 360;
  const mid = `hsl(${hue}, 52%, 32%)`;
  const dark = `hsl(${hue}, 58%, 20%)`;
  return `linear-gradient(135deg, ${mid} 50%, ${dark} 50%)`;
};

const clubInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();

const ClubCrest = ({
  name = "",
  logoUrl = null,
  variant = "shield",
  width = 13,
  height = 15,
  avatarSize = 44,
  className = "",
  title,
}) => {
  const gradient = crestGradient(name);
  const label = title || name || undefined;

  if (variant === "badge") {
    // Wrapper font-size drives the em-sized badge: 11px @44 avatar, 10px @40,
    // 9px @32 (matches the design's flag-docking scale).
    const wrapperFont =
      avatarSize >= 44 ? 11 : avatarSize >= 40 ? 10 : 9;
    return (
      <span
        className={`absolute ${className}`}
        style={{
          right: -3,
          bottom: -3,
          fontSize: wrapperFont,
          lineHeight: 1,
          background: "#fff",
          borderRadius: 4,
          boxShadow: "0 0 0 2px #fff",
        }}
        aria-label={label ? `${label} crest` : "Club crest"}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={label || "Club crest"}
            style={{
              width: "1.35em",
              height: "1.35em",
              borderRadius: "3px 3px 50% 50% / 3px 3px 58% 58%",
              objectFit: "cover",
              display: "inline-block",
              verticalAlign: "-0.25em",
              boxShadow: "0 0 0 1px rgba(16,24,40,.10)",
            }}
          />
        ) : (
          <span
            style={{
              display: "inline-block",
              width: "1.35em",
              height: "1.35em",
              background: gradient,
              borderRadius: "3px 3px 50% 50% / 3px 3px 58% 58%",
              verticalAlign: "-0.25em",
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,.35), 0 0 0 1px rgba(16,24,40,.10)",
            }}
          />
        )}
      </span>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={`flex-shrink-0 flex items-center justify-center text-white font-extrabold select-none ${className}`}
        style={{
          width: 60,
          height: 66,
          background: logoUrl ? undefined : gradient,
          borderRadius: "11px 11px 50% 50% / 11px 11px 58% 58%",
          boxShadow:
            "inset 0 0 0 3px rgba(255,255,255,.38), 0 2px 4px rgba(16,24,40,.2)",
          fontSize: 15,
          letterSpacing: ".02em",
          overflow: "hidden",
        }}
        aria-label={label ? `${label} crest` : "Club crest"}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={label || "Club crest"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          clubInitials(name) || "?"
        )}
      </div>
    );
  }

  // variant === "shield" (inline mark)
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={label || "Club crest"}
        className={`inline-block flex-shrink-0 ${className}`}
        style={{
          width,
          height,
          borderRadius: "3px 3px 50% 50% / 3px 3px 58% 58%",
          objectFit: "cover",
          boxShadow: "0 0 0 1px rgba(16,24,40,.10)",
        }}
      />
    );
  }
  return (
    <span
      className={`inline-block flex-shrink-0 ${className}`}
      style={{
        width,
        height,
        background: gradient,
        borderRadius: "3px 3px 50% 50% / 3px 3px 58% 58%",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,.35)",
      }}
      aria-label={label ? `${label} crest` : "Club crest"}
    />
  );
};

export default ClubCrest;
